import React, {AnchorHTMLAttributes, ButtonHTMLAttributes} from "react"
import {createUseStyles} from "react-jss"
import cn from "classnames"
import {LoadingOutlined} from "@ant-design/icons"
import {GlobalToken} from "antd/es/theme/interface"

export type ButtonTypes = "primary" | "ghost" | "danger" | "disabled"
export type ButtonShapes = "default" | "round" | "circle"
export type ButtonSizes = "large" | "middle" | "small"

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
  buttonType?: ButtonTypes
  block?: boolean
  loading?: boolean
  disabled?: boolean
  onClick?: () => void
  shape?: ButtonShapes
  size?: ButtonSizes
  icon?: React.ReactNode
  href?: string
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"]
}

const useStyles = createUseStyles<
  string,
  {
    type: ButtonTypes
    disabled: boolean
    block: boolean
    shape: ButtonShapes
    size: ButtonSizes
  },
  GlobalToken
>((theme) => ({
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 5,
    width: ({block}) => (block ? "100%" : "max-content"),
    padding: ({size, block}) => {
      if (block) {
        switch (size) {
          case "small":
            return "5px 0"
          case "middle":
            return "15px 0"
          case "large":
            return "20px 0"
          default:
            return "15px 0"
        }
      }
      switch (size) {
        case "small":
          return "5px 10px"
        case "middle":
          return "15px 20px"
        case "large":
          return "20px 25px"
        default:
          return "15px 20px"
      }
    },
    opacity: ({disabled}) => (disabled ? 0.7 : 1),
    fontWeight: 600,
    borderRadius: ({shape}) => {
      switch (shape) {
        case "circle":
          return "50%"
        case "round":
          return 100
        case "default":
          return theme.borderRadiusSM
        default:
          return theme.borderRadiusSM
      }
    },
    border: ({type}) =>
      type === "ghost" ? "1px solid rgba(54, 77, 104, 0.19)" : "none",
    backgroundColor: ({type, disabled}) => {
      if (disabled) {
        return "rgba(141, 141, 141, 1)"
      }
      switch (type) {
        case "primary":
          return theme.colorPrimary
        case "danger":
          return theme.colorError
        case "disabled":
          return "rgba(141, 141, 141, 1)"
        case "ghost":
          return "transparent"
        default:
          return theme.colorPrimary
      }
    },
    transition: "background .2s ease",
    color: ({type, disabled}) =>
      disabled
        ? "rgba(255, 255, 255, 1)"
        : type === "ghost"
          ? "rgba(54, 77, 104, 1)"
          : "rgba(255, 255, 255, 1)",
    cursor: ({disabled}) => (disabled ? "not-allowed" : "pointer"),
    "&:hover": {
      backgroundColor: ({type, disabled}) => {
        if (disabled) {
          return "rgba(141, 141, 141, 1)"
        }
        switch (type) {
          case "primary":
            return theme.colorPrimaryHover
          case "danger":
            return theme.colorError
          case "disabled":
            return "rgba(141, 141, 141, 1)"
          case "ghost":
            return "transparent"
          default:
            return theme.colorPrimaryHover
        }
      },
      color: ({type, disabled}) =>
        disabled
          ? "rgba(255, 255, 255, 1)"
          : type === "ghost"
            ? "rgba(54, 77, 104, 1)"
            : "rgba(255, 255, 255, 1)"
    },
    "& span": {
      background: "transparent !important",
      padding: 0
    }
  }
}))

const Button: React.FC<ButtonProps> = ({
  children,
  buttonType = "primary",
  block = false,
  loading = false,
  disabled = false,
  onClick,
  shape = "default",
  size = "middle",
  icon,
  className,
  href,
  target,
  ...props
}) => {
  const isDisabled = loading || disabled
  const classes = useStyles({
    type: buttonType,
    disabled: isDisabled,
    block,
    shape,
    size
  })

  if (href !== undefined) {
    return (
      <a href={href} target={target} className={cn(classes.button, className)}>
        {loading ? <LoadingOutlined /> : icon} {children}
      </a>
    )
  }

  return (
    <button
      onClick={onClick}
      className={cn(classes.button, className)}
      disabled={loading || isDisabled}
      {...props}
    >
      {loading ? <LoadingOutlined /> : icon} {children}
    </button>
  )
}

export default Button
